import { useQuery } from '@tanstack/react-query';
import { flows, identity, templates } from '@repletoryreach/sdk-api';
import type { ChecklistItem, Guide, GuideLink, TourStep } from '../../app/guides/types';
import { useCalendars } from './staff/calendars';
import { useBookingSettings, useServices, useStaff } from './lib/data';

// The Bookings guide (8 October 2026): the tour, the written guide and the setup checklist. The words are in the
// catalogue under guide.bookings.*; the pages it links to are the console's own routes.

const L = (key: string, to: string) => ({ labelKey: `guide.bookings.link.${key}`, to });
const LINKS = {
  hours: L('hours', '/settings/organisation'),
  team: L('team', '/settings/team'),
  staff: L('staff', '/bookings/staff'),
  services: L('services', '/bookings/services'),
  classes: L('classes', '/bookings/classes'),
  settings: L('settings', '/bookings/settings'),
  calendar: L('calendar', '/bookings'),
  list: L('list', '/bookings/list'),
  templates: L('templates', '/templates'),
  flows: L('flows', '/flows'),
  inbox: L('inbox', '/inbox'),
  channels: L('channels', '/channels'),
  numbers: L('numbers', '/numbers'),
  notifications: L('notifications', '/settings/notifications'),
  billing: L('billing', '/billing'),
  usage: L('usage', '/billing/usage'),
  tickets: L('tickets', '/tickets'),
  howAppointments: { labelKey: 'guide.bookings.link.howAppointments', tour: 'appointments' },
  howNoPerson: { labelKey: 'guide.bookings.link.howNoPerson', tour: 'no-person' }
};

const step = (id: string, route: string | undefined, target?: string, links?: GuideLink[]) => ({
  route, target, titleKey: `guide.bookings.tour.${id}.title`, bodyKey: `guide.bookings.tour.${id}.body`, links
});

const section = (id: string, paragraphs: number, notes: number, links: GuideLink[]) => ({
  id,
  titleKey: `guide.bookings.s.${id}.title`,
  bodyKeys: Array.from({ length: paragraphs }, (_, i) => `guide.bookings.s.${id}.p${i + 1}`),
  noteKeys: Array.from({ length: notes }, (_, i) => `guide.bookings.s.${id}.note${i + 1}`),
  links
});

/** One step of a how-to tour; steps inside the service form open it with the Add a service button first. */
const how = (tour: string, id: string, route: string, target: string, opts: { open?: string; links?: GuideLink[] } = {}): TourStep => ({
  route, target, open: opts.open, links: opts.links,
  titleKey: `guide.bookings.howto.${tour}.${id}.title`, bodyKey: `guide.bookings.howto.${tour}.${id}.body`
});
const FORM = { open: 'bookings-services-add' };

/** Whether the business has set up each part, from what is really there. */
function useBookingsChecklist(): { items: ChecklistItem[]; loading: boolean } {
  const hours = useQuery({ queryKey: ['business-hours'], queryFn: () => identity.getBusinessHours(), staleTime: 60_000 });
  const staff = useStaff();
  const services = useServices(false);
  const settings = useBookingSettings();
  const calendars = useCalendars();
  const tpl = useQuery({ queryKey: ['templates'], queryFn: () => templates.list(), staleTime: 60_000 });
  const flowList = useQuery({ queryKey: ['flows', 'guide'], queryFn: () => flows.listFlows(), staleTime: 60_000 });

  const people = staff.data?.data.filter((s) => s.active) ?? [];
  const bookable = (services.data?.data ?? []).filter((s) => s.active && (s.kind !== 'appointment' || s.staff_mode === 'none' || s.staff_ids.length > 0));
  const approved = new Set((tpl.data?.data ?? []).filter((x) => x.status === 'approved').map((x) => x.code));
  const hasBookingStep = (flowList.data?.data ?? []).some((f) => f.is_active && JSON.stringify(f.definition ?? {}).includes('"type":"booking"'));
  const s = settings.data?.data;

  const item = (id: string, to: string, done: boolean | undefined, optional = false): ChecklistItem => ({
    id, to, done, optional, titleKey: `guide.bookings.check.${id}.title`, hintKey: `guide.bookings.check.${id}.hint`
  });
  const items = [
    item('hours', '/settings/organisation', hours.data ? hours.data.data.configured : undefined),
    item('people', '/bookings/staff', staff.data ? people.length > 0 : undefined),
    item('services', '/bookings/services', services.data ? bookable.length > 0 : undefined),
    item('templates', '/templates', tpl.data ? ['booking_confirmation', 'booking_reminder', 'booking_update'].every((c) => approved.has(c)) : undefined),
    item('chatbot', '/flows', flowList.data ? hasBookingStep : undefined),
    item('page', '/bookings/settings', s ? Boolean(s.public_page_enabled && s.public_slug) : undefined, true),
    item('calendar', '/bookings/staff', calendars.data ? calendars.data.data.connections.length > 0 : undefined, true)
  ];
  return { items, loading: [hours, staff, services, settings, tpl, flowList].some((q) => q.isPending) };
}

export const BOOKINGS_GUIDE: Guide = {
  id: 'bookings',
  version: 1,
  titleKey: 'guide.bookings.title',
  introKey: 'guide.bookings.intro',
  routes: ['/bookings'],
  tour: [
    step('welcome', undefined, undefined, [LINKS.hours]),
    step('people', '/bookings/staff', 'bookings-staff-add,bookings-staff-list', [LINKS.hours, LINKS.team]),
    step('sync', '/bookings/staff', 'bookings-calendar-sync'),
    step('services', '/bookings/services', 'bookings-services-add,bookings-services-list', [LINKS.howAppointments, LINKS.howNoPerson]),
    step('classes', '/bookings/classes', 'bookings-classes-add,bookings-classes-list'),
    step('times', '/bookings/settings?section=times', 'bookings-settings-times'),
    step('reminders', '/bookings/settings?section=reminders', 'bookings-settings-reminders'),
    step('confirmations', '/bookings/settings?section=confirmations', 'bookings-settings-confirmations', [LINKS.templates]),
    step('page', '/bookings/settings?section=page', 'bookings-settings-public'),
    step('clients', '/bookings', undefined, [LINKS.flows, LINKS.inbox, LINKS.channels]),
    step('calendar', '/bookings', 'bookings-calendar-grid'),
    step('new', '/bookings', 'bookings-new'),
    step('list', '/bookings/list', 'bookings-list-filters', [LINKS.notifications]),
    step('help', '/bookings/list', 'bookings-help')
  ],
  howTos: [
    {
      id: 'appointments',
      titleKey: 'guide.bookings.howto.appointments.title',
      hintKey: 'guide.bookings.howto.appointments.hint',
      steps: [
        how('appointments', 'people', '/bookings/staff', 'bookings-staff-add,bookings-staff-list', { links: [LINKS.hours] }),
        how('appointments', 'times', '/bookings/settings?section=times', 'bookings-settings-times'),
        how('appointments', 'name', '/bookings/services', 'service-name', FORM),
        how('appointments', 'kind', '/bookings/services', 'service-kind', FORM),
        how('appointments', 'length', '/bookings/services', 'service-length', FORM),
        how('appointments', 'where', '/bookings/services', 'service-where', FORM),
        how('appointments', 'approval', '/bookings/services', 'service-approval', FORM),
        how('appointments', 'who', '/bookings/services', 'service-who', FORM),
        how('appointments', 'people2', '/bookings/services', 'service-people,service-who', FORM),
        how('appointments', 'questions', '/bookings/services', 'service-questions', FORM),
        how('appointments', 'save', '/bookings/services', 'service-save', { ...FORM, links: [LINKS.flows, LINKS.settings] })
      ]
    },
    {
      id: 'no-person',
      titleKey: 'guide.bookings.howto.noPerson.title',
      hintKey: 'guide.bookings.howto.noPerson.hint',
      steps: [
        how('noPerson', 'name', '/bookings/services', 'service-name', FORM),
        how('noPerson', 'kind', '/bookings/services', 'service-kind', FORM),
        how('noPerson', 'length', '/bookings/services', 'service-length', FORM),
        how('noPerson', 'who', '/bookings/services', 'service-who', FORM),
        how('noPerson', 'atOnce', '/bookings/services', 'service-at-once,service-who', FORM),
        how('noPerson', 'save', '/bookings/services', 'service-save', { ...FORM, links: [LINKS.hours] })
      ]
    }
  ],
  sections: [
    section('overview', 3, 1, [LINKS.calendar, LINKS.list]),
    section('before', 2, 2, [LINKS.hours, LINKS.team]),
    section('people', 3, 2, [LINKS.staff, LINKS.team]),
    section('services', 4, 2, [LINKS.services, LINKS.howAppointments, LINKS.howNoPerson, LINKS.billing]),
    section('slots', 3, 1, [LINKS.howAppointments, LINKS.settings, LINKS.staff]),
    section('classes', 2, 1, [LINKS.classes, LINKS.services]),
    section('settings', 3, 2, [LINKS.settings]),
    section('clients', 4, 2, [LINKS.flows, LINKS.channels, LINKS.inbox, LINKS.settings]),
    section('messages', 3, 2, [LINKS.templates, LINKS.numbers, LINKS.usage]),
    section('daily', 3, 1, [LINKS.calendar, LINKS.list, LINKS.notifications]),
    section('sync', 2, 2, [LINKS.staff]),
    section('tickets', 1, 0, [LINKS.tickets]),
    section('trouble', 5, 0, [LINKS.hours, LINKS.staff, LINKS.templates, LINKS.billing])
  ],
  useChecklist: useBookingsChecklist
};
